/* eslint-disable custom-eslint-rules/force-logicalOperandsMustBeBoolean */
import styled from "styled-components";
import { ProgressBarProps, StyleProgressBarProps } from "./ProgressBar.types.js";
import { ProgressTextContainer } from "./components/ProgressText/style.js";

export const ProgressBackground = styled.div<{ backgroundColor?: string; height?: string }>`
    background-color: ${({ backgroundColor, ...props }) => backgroundColor || props.theme.grayscale[10]};
    width: 100%;
    height: ${({ theme, height }) => height || theme.rem(0.75)};
    overflow: hidden;
`;

export const Progress = styled.div<Pick<StyleProgressBarProps, "color" | "value"> & { indeterminate?: boolean }>`
    background-color: ${({ color, theme }) =>
        color === "information" || color === "title_active" || color === "default" ? theme.colors.primary : theme.colors[color ?? "primary"][100]};
    width: ${(props) => (props.indeterminate ? "100" : props.value)}%;
    max-width: 100%;
    height: 100%;
    transition:
        width 0.5s ease-in-out,
        background-color 0.5s ease-in-out;
    animation: ${({ indeterminate }) => (indeterminate ? "IndeterminateProgress 2s infinite" : undefined)};

    @keyframes IndeterminateProgress {
        0%,
        17.5% {
            transform: translateX(-100%);
        }
        82.5%,
        100% {
            transform: translateX(100%);
        }
    }
`;

export const ProgressBarContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => `${theme.spacing[8]}`};

    ${ProgressBackground}, ${Progress} {
        border-radius: ${({ theme }) => theme.radius.xs};
    }

    ${ProgressTextContainer} {
        display: flex;
        align-items: center;
    }
`;

export const SVG = styled.svg.attrs({
    viewBox: "-1 -1 34 34",
})``;

export const ProgressCircle = styled.circle.attrs({
    cx: "16",
    cy: "16",
    r: "15.4",
})<
    { indeterminate?: boolean; backgroundColor?: string } & (
        | {
              type: "progress";
              color: StyleProgressBarProps["color"];
              value: StyleProgressBarProps["value"];
          }
        | {
              type: "background";
              color?: never;
              value?: never;
          }
    )
>`
    fill: none;
    stroke-dasharray: 100 100;
    stroke-dashoffset: ${({ type, indeterminate, ...props }) =>
        type === "progress" ? (indeterminate ? 100 : 100 - (props as { value: number }).value) : 0};
    stroke-linecap: round;
    stroke-width: ${({ theme }) => theme.rem(0.2)};
    stroke: ${({ type, backgroundColor, theme, ...props }) =>
        type === "background" || props.color === "default" || props.color === "title_active" || props.color === "information"
            ? backgroundColor || "white"
            : theme?.colors[props.color ?? "primary"][100]};
    transition:
        stroke-dashoffset 0.5s ease-in-out,
        stroke 0.5s ease-in-out;
    animation: ${({ indeterminate }) => (indeterminate ? "IndeterminateProgressRounded 2.5s infinite" : undefined)};

    @keyframes IndeterminateProgressRounded {
        0%,
        17.5% {
            stroke-dashoffset: 100;
        }
        50% {
            stroke-dashoffset: 0;
        }
        82.5%,
        100% {
            stroke-dashoffset: -100;
        }
    }
`;

const sizeMap: Record<NonNullable<ProgressBarProps["size"]>, { width: number; height: number }> = {
    small: { width: 4, height: 4 },
    medium: { width: 6, height: 6 },
    large: { width: 8, height: 8 },
};

export const StyledCircleProgressBar = styled.div<{ ref: React.ForwardedRef<HTMLDivElement>; size: NonNullable<ProgressBarProps["size"]> }>`
    position: relative;
    display: inline-block;
    text-align: center;

    ${SVG} {
        transform: rotate(-90deg);
        width: ${({ theme, size }) => theme.rem(sizeMap[size].width)};
        height: ${({ theme, size }) => theme.rem(sizeMap[size].height)};
    }

    ${ProgressTextContainer} {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
    }
`;
